import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import Modal from '../../common/Modal/Modal';
import styles from './ViewRoutingModal.module.scss';
import { useTranslation } from 'react-i18next';
import ViewRoutingTable from './ViewRoutingTable/ViewRoutingTable';
import Button from '../../common/Button/Button';
import {
  Approver,
  useViewRoutingContext,
  ViewRoutingProvider,
} from './ViewRoutingContext/ViewRoutingContext';
import AddApproverCell from './AddApproverCell/AddApproverCell';
import {
  AwardApprover,
  HATVAwardRoutingDetails,
  useGetAwardRoutingDetailsQuery,
  useGetUsersListByNativeIDsQuery,
  UserItem,
  useResetAwardRoutingMutation,
  useCreateAwardRoutingMutation,
  useBulkCreateAwardRoutingMutation,
} from '../../../redux/services/hatvAwardApprovalAPI';
import ViewRoutingLoader from './ViewRoutingLoader/ViewRoutingLoader';
import { HATV_AWARD_ROUTING_STATUS } from '../../../constants/dataConstants';
import ToastMessage from '../../common/ToastMessage/ToastMessage';

const ViewRoutingModalStyles = {
  minWidth: '660px',
  overflow: 'visible',
};

const ViewRoutingModalContainerStyles = {
  padding: 0,
};

interface ViewRoutingModalProps {
  selectedAbos: TableRowData[];
  performanceYear: string;
  setIsStatusUpdate?: (apiStatus: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const ViewRoutingModal: React.FC<PropsWithChildren<ViewRoutingModalProps>> = ({
  children,
  selectedAbos,
  performanceYear,
  setIsStatusUpdate,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [localReset, setLocalReset] = useState(false);

  const isControlled = isOpen !== undefined;
  const showModal = isControlled ? isOpen : internalOpen;
  const firstAbo = selectedAbos[0];
  const routingRoleIndicators = firstAbo?.routingRoleIndicators;

  const { data, isFetching, isError } = useGetAwardRoutingDetailsQuery(
    {
      affiliateCode: firstAbo?.affiliateCode,
      aboNumber: firstAbo?.aboNumber,
      awardCode: firstAbo?.trackingAward?.awardCode || firstAbo?.currentAward?.awardCode,
      performanceYear,
      awardQualifiedMonth: firstAbo?.trackingAward?.awardQualifiedMonth,
    },
    { skip: !showModal || !firstAbo || !performanceYear }
  );

  const [toastConfig, setToastConfig] = useState({
    message: '',
    type: 'success' as 'success' | 'warn' | 'error',
    show: false,
  });

  const setShowToast = (show: boolean) => setToastConfig((prev) => ({ ...prev, show }));

  const nativeIDs = data ? getNativeIdsFromRes(data) : [];

  const { data: userDetailsRes, isFetching: isUserListLoading } = useGetUsersListByNativeIDsQuery(
    {
      nativeIDs,
    },
    { skip: !nativeIDs.length }
  );

  const [createAwardRouting, createResult] = useCreateAwardRoutingMutation();
  const [bulkCreateAwardRouting, bulkCreateResult] = useBulkCreateAwardRoutingMutation();
  const [resetAwardRouting, resetResult] = useResetAwardRoutingMutation();

  const { approvers, setApprovers, viewOnly, setViewOnly } = useViewRoutingContext();

  const resetApproversList = useCallback(() => {
    if (!data) return;
    if (!userDetailsRes?.body) return;
    setApprovers(
      data.award?.awardApprovers
        ? data.award.awardApprovers
            .slice()
            .sort((a, b) => a.approvalSequence - b.approvalSequence)
            .map((approver) => mapAwardApproverToApproverItem(approver, userDetailsRes.body))
        : []
    );
  }, [data, setApprovers, userDetailsRes]);

  useEffect(() => {
    resetApproversList();
  }, [resetApproversList]);

  useEffect(() => {
    if (isError) {
      setToastConfig({
        message: 'Failed to fetch Award details. Please try again.',
        type: 'error',
        show: true,
      });
    }
  }, [isError]);

  useEffect(() => {
    const results = [createResult, bulkCreateResult, resetResult];
    const successResult = results.find(r => r.isSuccess);
    const errorResult = results.find(r => r.isError);

    if (errorResult) {
      setToastConfig({
        message: 'Failed to save Award Approvers routing. Please try again.',
        type: 'error',
        show: true,
      });
      setIsStatusUpdate && setIsStatusUpdate('fail');
    }

    if (successResult) {
      setToastConfig({
        message: 'Successfully saved Award Approvers routing.',
        type: 'success',
        show: true,
      });
      setIsStatusUpdate && setIsStatusUpdate('success');
    }
  }, [createResult, bulkCreateResult, resetResult, setIsStatusUpdate]);

  const hatvAdmin = userDetailsRes?.body.find((u) => u.nativeId === data?.hatvAdmin?.userNativeId);

  const isReset = data && data.award?.finalApprovalStatus !== HATV_AWARD_ROUTING_STATUS.READY;

  const isAuthorizedToAct = isReset
    ? routingRoleIndicators?.isAuthorizedUpdateResetRouting ?? false
    : routingRoleIndicators?.isAuthorizedCreateRouting ?? false;

  useEffect(() => {
    if (isReset) {
      setViewOnly(!isAuthorizedToAct || !localReset);
    } else {
      setViewOnly(!isAuthorizedToAct);
    }
  }, [routingRoleIndicators, isReset, setViewOnly, isAuthorizedToAct, localReset]);

  const handleClickRoute = async () => {
    if (selectedAbos.length === 0 || !data) return;

    if (isReset && !localReset) {
      setLocalReset(true);
      setApprovers([]);
      return;
    }

    const awardApprovers = approvers.map((appr, index) => ({
      approvalSequence: index + 1,
      userNativeId: appr.id,
    }));

    if (isReset) {
      await resetAwardRouting({
        aboNumber: firstAbo.aboNumber,
        affiliateCode: firstAbo.affiliateCode,
        payload: {
          aboHatvAwardId: data.award?.aboHatvAwardId || 0,
          performanceYear: +performanceYear,
          awardCode: firstAbo.trackingAward?.awardCode || firstAbo.currentAward.awardCode,
          awardQualifiedMonth: firstAbo.trackingAward?.awardQualifiedMonth ?? '',
          awardQualifiedYear: firstAbo.trackingAward?.awardQualifiedYear ?? '',
          awardApprovers,
        },
      });
    } else if (selectedAbos.length > 1) {
      await bulkCreateAwardRouting({
        payload: {
          performanceYear: +performanceYear,
          awards: selectedAbos.map((abo) => ({
            affiliateCode: abo.affiliateCode,
            aboNumber: abo.aboNumber,
            awardCode: abo.trackingAward?.awardCode || abo.currentAward.awardCode,
            awardTagCode: String(abo.trackingAward?.awardTagCode ?? ''),
            awardQualifiedMonth: abo.trackingAward?.awardQualifiedMonth ?? '',
          })),
          awardApprovers,
        },
      });
    } else {
      await createAwardRouting({
        affiliateCode: firstAbo.affiliateCode,
        aboNumber: firstAbo.aboNumber,
        payload: {
          performanceYear: +performanceYear,
          awardCode: firstAbo.trackingAward?.awardCode || firstAbo.currentAward.awardCode,
          awardTagCode: String(firstAbo.trackingAward?.awardTagCode ?? ''),
          awardQualifiedMonth: firstAbo.trackingAward?.awardQualifiedMonth ?? '',
          awardApprovers,
        },
      });
    }
  };
  const onModalClose = () => {
    if (isControlled) {
      onClose?.();
    } else {
      setInternalOpen(false);
    }
    resetApproversList();
    setLocalReset(false);
    createResult.reset();
    bulkCreateResult.reset();
    resetResult.reset();
  };

  const routingStatus = data?.award
    ? t(`hatv_view_routing.routing_status.${data.award.finalApprovalStatus}`)
    : null;

  const isLoading =
    isFetching || 
    isUserListLoading || 
    createResult.isLoading || 
    bulkCreateResult.isLoading || 
    resetResult.isLoading;

  return (
    <>
      {!isControlled && (
        <div
          className={styles.action_btn}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setInternalOpen(true);
          }}
        >
          {children}
        </div>
      )}
      <Modal
        showDrawer={showModal}
        onClose={onModalClose}
        customStyle={ViewRoutingModalStyles}
        customContainerStyle={ViewRoutingModalContainerStyles}
      >
        <div className={`view_routing_modal ${styles['modal']}`}>
          {isLoading ? (
            <ViewRoutingLoader />
          ) : (
            <>
              <header>
                <h1>{t('hatv_view_routing.heading')}</h1>
                <div className={`routing-info ${styles['routing-info']}`}>
                  <div className={`info-item ${styles['info-item']} ${styles.longText}`}>
                    <h2>{data?.name || selectedAbos[0].name}</h2>
                    <p>{data?.aboNumber || selectedAbos[0].aboNumber}</p>
                  </div>
                  <div className={`info-item ${styles['info-item']}`}>
                    <h2>{t('hatv_view_routing.subheading_award')}:</h2>
                    <p>{data?.award?.awardName || selectedAbos[0].currentAward.awardName}</p>
                  </div>
                  <div className={`info-item ${styles['info-item']}`}>
                    <h2>{t('hatv_view_routing.subheading_approval_status')}:</h2>
                    <p>{routingStatus || '-'}</p>
                  </div>
                  <div className={`info-item ${styles['info-item']}`}>
                    <h2>{t('hatv_view_routing.subheading_hatv_admin')}:</h2>
                    <p>{hatvAdmin?.fullName || '-'}</p>
                  </div>
                </div>
              </header>
              <div>
                <ViewRoutingTable />
                {!viewOnly && <AddApproverCell disabled={isError} />}
              </div>
              {isAuthorizedToAct && (
                <footer>
                  <Button
                    id="cancelButton"
                    onClick={onModalClose}
                    label={t('hatv_view_routing.btnCancel')}
                    style="unfilled"
                  />
                  <Button
                    id="routeButton"
                    onClick={handleClickRoute}
                    label={
                      isReset && !localReset
                        ? t('hatv_view_routing.btnReset')
                        : t('hatv_view_routing.btnRoute')
                    }
                    style="filled"
                    isDisabled={
                      createResult.isLoading || 
                      bulkCreateResult.isLoading || 
                      resetResult.isLoading || 
                      approvers.length <= 0
                    }
                  />
                </footer>
              )}
            </>
          )}
        </div>
      </Modal>
      <ToastMessage
        message={toastConfig.message}
        toastType={toastConfig.type}
        showToast={toastConfig.show}
        setShowToast={setShowToast}
        customStyles={{
          top: '64px',
        }}
        autoDismissal
      />
    </>
  );
};

const ViewRoutingModalWrapper: React.FC<PropsWithChildren<ViewRoutingModalProps>> = ({
  children,
  ...otherProps
}) => {
  const firstAbo = otherProps.selectedAbos[0];

  const aboDetails = {
    name: firstAbo?.name ?? '',
    affiliateCode: firstAbo?.affiliateCode ?? '',
    aboNumber: firstAbo?.aboNumber ?? '',
    awardCode: firstAbo?.trackingAward?.awardCode || firstAbo?.currentAward?.awardCode || '',
    awardName: firstAbo?.trackingAward?.awardName || firstAbo?.currentAward?.awardName || '',
    awardTagCode: firstAbo?.trackingAward?.awardTagCode ?? 0,
    awardQualifiedMonth: firstAbo?.trackingAward?.awardQualifiedMonth ?? '',
  };

  return (
    <ViewRoutingProvider aboDetails={aboDetails}>
      <ViewRoutingModal {...otherProps}>{children}</ViewRoutingModal>
    </ViewRoutingProvider>
  );
};

const getNativeIdsFromRes = (data: HATVAwardRoutingDetails) => {
  const nativeIds = new Set<string>();

  if (data?.accountManager?.userNativeId) nativeIds.add(data.accountManager.userNativeId);

  if (data?.hatvAdmin?.userNativeId) nativeIds.add(data.hatvAdmin.userNativeId);

  data?.award?.awardApprovers?.forEach((approver) => {
    if (approver.userNativeId) nativeIds.add(approver.userNativeId);
  });
  return Array.from(nativeIds);
};

const mapAwardApproverToApproverItem = (
  awardApprover: AwardApprover,
  usersList: UserItem[]
): Approver => {
  const userDetails = usersList.find((u) => u.nativeId === awardApprover.userNativeId);
  return {
    date: awardApprover.approvalDate,
    deleted: false,
    designation: 'TBD',
    name: userDetails?.fullName || '-',
    id: awardApprover.userNativeId,
    status: awardApprover.approvalStatus,
  };
};

export default ViewRoutingModalWrapper;
